import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { GitHubAuthButton } from '@/components/auth/GitHubAuthButton';
import { UserMenu } from '@/components/auth/UserMenu';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Save,
  Share2,
  Globe,
  Lock,
  ChevronDown,
  GitFork,
  History,
  Download,
  Loader2,
  Check,
  Plus,
  Compass,
  Moon,
  Sun,
  Sparkles,
  Camera,
  Eye,
  HelpCircle,
  PanelLeft,
  PanelRight,
  Columns,
  CheckCircle2,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { MeshpitLogo } from '@/components/MeshpitLogo';
import { HelpDialog } from '@/components/help/HelpDialog';
import type { ProductScore, ScoreCriterion } from '@/hooks/useProductScore';

// ---------------------------------------------------------------------------
// Product readiness score ring — SVG circular progress indicator
// ---------------------------------------------------------------------------

const RING_SIZE = 16;
const STROKE = 2;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function tierColor(tier: ProductScore['tier']): string {
  if (tier === 'high') return '#22c55e'; // green-500
  if (tier === 'mid') return '#f59e0b';  // amber-400
  return '#ef4444';                       // red-500
}

function ProductScoreRing({ score }: { score: ProductScore }) {
  const offset = CIRCUMFERENCE - (score.pct / 100) * CIRCUMFERENCE;
  const color = tierColor(score.tier);

  return (
    <span
      className="relative flex items-center justify-center"
      style={{ width: RING_SIZE, height: RING_SIZE }}
    >
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        className="absolute inset-0 -rotate-90"
        aria-hidden
      >
        {/* Track */}
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE}
          className="text-muted-foreground/20"
        />
        {/* Progress arc */}
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.4s ease, stroke 0.4s ease' }}
        />
      </svg>
      {/* Icon centred inside ring */}
      <Eye className="relative h-2.5 w-2.5" style={{ color }} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Rich score tooltip content
// ---------------------------------------------------------------------------

function CriterionRow({ criterion }: { criterion: ScoreCriterion }) {
  return (
    <div className="flex items-start gap-2">
      {criterion.met ? (
        <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-green-400" />
      ) : (
        <XCircle className="mt-px h-3.5 w-3.5 shrink-0 text-zinc-600" />
      )}
      <span className={criterion.met ? 'text-zinc-200' : 'text-zinc-500'}>
        {criterion.label}
      </span>
      {!criterion.met && criterion.weight >= 3 && (
        <span className="ml-auto shrink-0 text-[10px] font-medium text-amber-400">
          key
        </span>
      )}
    </div>
  );
}

function ProductScoreTooltipContent({ score }: { score: ProductScore }) {
  const color = tierColor(score.tier);
  const firstUnmet = score.criteria.find((c) => !c.met);
  // Sort: met first, then unmet by descending weight
  const sorted = [...score.criteria].sort((a, b) => {
    if (a.met !== b.met) return a.met ? -1 : 1;
    return b.weight - a.weight;
  });

  return (
    <div className="w-72 space-y-3 p-1 text-xs">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-zinc-100">Product Readiness</span>
        <span className="tabular-nums font-bold" style={{ color }}>
          {score.score} / {score.max}
        </span>
      </div>

      {/* Dot bar */}
      <div className="flex gap-0.5">
        {score.criteria.map((c) => (
          <div
            key={c.key}
            className="h-1.5 flex-1 rounded-full transition-colors duration-300"
            style={{
              backgroundColor: c.met ? color : 'rgba(255,255,255,0.1)',
            }}
            title={c.label}
          />
        ))}
      </div>

      {/* Criterion checklist */}
      <div className="space-y-1.5">
        {sorted.map((c) => (
          <CriterionRow key={c.key} criterion={c} />
        ))}
      </div>

      {/* Next step tip */}
      {firstUnmet && (
        <>
          <div className="border-t border-zinc-700" />
          <div className="flex items-start gap-2 rounded-md bg-zinc-800 px-2 py-1.5">
            <ArrowRight className="mt-px h-3.5 w-3.5 shrink-0 text-amber-400" />
            <span className="text-zinc-400">{firstUnmet.tip}</span>
          </div>
        </>
      )}
    </div>
  );
}

export interface EditorToolbarProps {
  title: string;
  onTitleChange: (title: string) => void;
  isPublic: boolean;
  onVisibilityChange: (isPublic: boolean) => void;
  onSave: () => void;
  onSaveAs?: () => void;
  onFork?: () => void;
  onShare: () => void;
  onShowHistory?: () => void;
  onDownload?: () => void;
  onAiAssist?: () => void;
  onScreenshot?: () => void;
  onProductMode?: () => void;
  onNewModel?: () => void;
  thumbnailUrl?: string | null;
  isSaving?: boolean;
  isDirty?: boolean;
  lastSavedAt?: string | null;
  isOwner?: boolean;
  modelSlug?: string | null;
  authorUsername?: string | null;
  layout?: 'code-preview' | 'all' | 'preview-parameters';
  onLayoutChange?: (
    layout: 'code-preview' | 'all' | 'preview-parameters',
  ) => void;
  /**
   * Controlled open state for the Help dialog.
   * When provided, the dialog is controlled from outside (e.g. via F1 shortcut).
   * If omitted, the dialog manages its own open state via the toolbar button.
   */
  helpOpen?: boolean;
  onHelpOpenChange?: (open: boolean) => void;
  /** Product readiness score — when provided, replaces the plain Product button with a scored ring button. */
  productScore?: ProductScore;
}

export function EditorToolbar({
  title,
  onTitleChange,
  isPublic,
  onVisibilityChange,
  onSave,
  onSaveAs,
  onFork,
  onShare,
  onShowHistory,
  onDownload,
  onAiAssist,
  onScreenshot,
  onProductMode,
  onNewModel,
  thumbnailUrl,
  isSaving = false,
  isDirty = false,
  lastSavedAt,
  isOwner = true,
  modelSlug,
  authorUsername,
  layout,
  onLayoutChange,
  helpOpen: controlledHelpOpen,
  onHelpOpenChange: onControlledHelpOpenChange,
  productScore,
}: EditorToolbarProps) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [internalHelpOpen, setInternalHelpOpen] = useState(false);

  // Support both controlled (from parent via F1) and uncontrolled (toolbar button) modes
  const showHelp = controlledHelpOpen ?? internalHelpOpen;
  const setShowHelp = (open: boolean) => {
    setInternalHelpOpen(open);
    onControlledHelpOpenChange?.(open);
  };

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const formatLastSaved = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div
      className={`flex h-11 items-center justify-between border-b border-border bg-background bg-zinc-${resolvedTheme === 'dark' ? '800' : '300'} px-3 pt-1`}
    >
      {/* Left side - Logo, Navigation, Title */}
      <div className="flex items-center gap-1">
        {/* Logo */}
        <Link to="/" className="mr-2 flex items-center">
          <MeshpitLogo className="h-6 w-6 text-foreground" />
          <span className="ml-2 mr-2">Meshpit</span>
        </Link>

        <Separator orientation="vertical" className="mx-1 h-5" />

        {/* New Model */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 px-2"
              onClick={() => {
                if (onNewModel) {
                  onNewModel();
                } else {
                  navigate('/new');
                }
              }}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>New Model</TooltipContent>
        </Tooltip>

        {/* Explore */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 px-2"
              asChild
            >
              <Link to="/explore">
                <Compass className="h-4 w-4" />
                <span className="hidden sm:inline">Explore</span>
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Explore Models</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="mx-1 h-5" />

        {/* Title */}
        <div className="flex items-center gap-2">
          {/* Tiny thumbnail */}
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt="Thumbnail"
              className="h-6 w-6 rounded border border-border object-cover"
            />
          )}
          {isEditingTitle ? (
            <Input
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              onBlur={() => setIsEditingTitle(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setIsEditingTitle(false);
                if (e.key === 'Escape') setIsEditingTitle(false);
              }}
              className="h-8 w-64"
              autoFocus
              disabled={!isOwner}
            />
          ) : (
            <button
              onClick={() => isOwner && setIsEditingTitle(true)}
              className={`text-sm font-medium text-foreground ${
                isOwner
                  ? 'cursor-text hover:text-muted-foreground'
                  : 'cursor-default'
              }`}
              title={isOwner ? 'Click to edit title' : undefined}
            >
              {title || 'Untitled Model'}
            </button>
          )}

          {/* Dirty indicator */}
          {isDirty && <span className="text-sm text-muted-foreground">*</span>}
        </div>

        {/* Author (if viewing someone else's model) */}
        {authorUsername && !isOwner && (
          <span className="text-sm text-muted-foreground">
            by{' '}
            <a
              href={`/${authorUsername}`}
              className="text-foreground hover:underline"
            >
              @{authorUsername}
            </a>
          </span>
        )}

        {/* Visibility Toggle */}
        {isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2">
                {isPublic ? (
                  <>
                    <Globe className="h-3.5 w-3.5" />
                    <span className="text-xs">Public</span>
                  </>
                ) : (
                  <>
                    <Lock className="h-3.5 w-3.5" />
                    <span className="text-xs">Private</span>
                  </>
                )}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onClick={() => onVisibilityChange(true)}
                className="cursor-pointer"
              >
                <Globe className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Public</span>
                  <span className="text-xs text-muted-foreground">
                    Anyone can view
                  </span>
                </div>
                {isPublic && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onVisibilityChange(false)}
                className="cursor-pointer"
              >
                <Lock className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Private</span>
                  <span className="text-xs text-muted-foreground">
                    Only you can view
                  </span>
                </div>
                {!isPublic && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Public badge for non-owners */}
        {!isOwner && (
          <Badge variant="outline">
            {isPublic ? (
              <>
                <Globe className="mr-1 h-3 w-3" /> Public
              </>
            ) : (
              <>
                <Lock className="mr-1 h-3 w-3" /> Private
              </>
            )}
          </Badge>
        )}

        {/* Last saved indicator */}
        {lastSavedAt && (
          <span className="text-xs text-muted-foreground">
            Saved {formatLastSaved(lastSavedAt)}
          </span>
        )}
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-1">
        {/* Layout Switch */}
        {onLayoutChange && layout && (
          <>
            <ToggleGroup
              type="single"
              value={layout}
              onValueChange={(value) => {
                if (value)
                  onLayoutChange(
                    value as 'code-preview' | 'all' | 'preview-parameters',
                  );
              }}
              className="mr-2 hidden gap-0 md:flex"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem
                    value="code-preview"
                    aria-label="Code and Preview"
                    size="sm"
                    className="h-8 w-6 p-0"
                  >
                    <PanelLeft className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Code & Preview</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem
                    value="all"
                    aria-label="All Panels"
                    size="sm"
                    className="h-8 w-6 p-0"
                  >
                    <Columns className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>All Panels</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem
                    value="preview-parameters"
                    aria-label="Preview and Parameters"
                    size="sm"
                    className="h-8 w-6 p-0"
                  >
                    <PanelRight className="h-4 w-4" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>Preview & Parameters</TooltipContent>
              </Tooltip>
            </ToggleGroup>
            <Separator orientation="vertical" className="mx-1 h-5" />
          </>
        )}

        {/* Product Mode button */}
        {onProductMode && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onProductMode}
                className="h-8 gap-1.5 px-2"
              >
                {productScore ? (
                  <ProductScoreRing score={productScore} />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Product</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              sideOffset={6}
              className={productScore ? 'bg-zinc-900 border border-zinc-700 p-2' : undefined}
            >
              {productScore ? (
                <ProductScoreTooltipContent score={productScore} />
              ) : (
                'Product Mode'
              )}
            </TooltipContent>
          </Tooltip>
        )}

        {/* AI Assistant button */}
        {onAiAssist && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onAiAssist}
                className="h-8 gap-1.5 px-2"
              >
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">AI</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>AI Assistant</TooltipContent>
          </Tooltip>
        )}

        {/* Screenshot button */}
        {onScreenshot && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onScreenshot}
                className="h-8 gap-1.5 px-2"
              >
                <Camera className="h-4 w-4" />
                <span className="hidden sm:inline">Screenshot</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Capture Thumbnail</TooltipContent>
          </Tooltip>
        )}

        {/* History button */}
        {onShowHistory && modelSlug && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onShowHistory}
                className="h-8 w-8"
              >
                <History className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Version History</TooltipContent>
          </Tooltip>
        )}

        {/* Download button */}
        {onDownload && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onDownload}
                className="h-8 w-8"
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Download STL</TooltipContent>
          </Tooltip>
        )}

        {/* Fork button (for non-owners) */}
        {!isOwner && onFork && isAuthenticated && (
          <Button
            variant="outline"
            size="sm"
            onClick={onFork}
            className="gap-1.5"
          >
            <GitFork className="h-4 w-4" />
            Fork
          </Button>
        )}

        {/* Share button */}
        {isPublic && (
          <Button
            variant="outline"
            size="sm"
            onClick={onShare}
            className="gap-1.5"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        )}

        {/* Save button (for owners) */}
        {isOwner && isAuthenticated && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-1.5" disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={onSave}
                disabled={isSaving}
                className="cursor-pointer"
              >
                <Save className="mr-2 h-4 w-4" />
                Save
                <span className="ml-auto text-xs text-muted-foreground">
                  Cmd+S
                </span>
              </DropdownMenuItem>
              {onSaveAs && (
                <DropdownMenuItem
                  onClick={onSaveAs}
                  disabled={isSaving}
                  className="cursor-pointer"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save As New...
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onFork}
                disabled={isSaving || !modelSlug}
                className="cursor-pointer"
              >
                <GitFork className="mr-2 h-4 w-4" />
                Fork to New Model
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Auth */}
        {isLoading ? (
          <div className="h-7 w-7 animate-pulse rounded-full bg-muted" />
        ) : isAuthenticated ? (
          <UserMenu collapsed />
        ) : (
          <GitHubAuthButton
            variant="ghost"
            size="sm"
            showText={true}
            className="h-8 w-40 p-0"
          />
        )}

        <Separator orientation="vertical" className="mx-1 h-5" />

        {/* Help */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowHelp(true)}
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Help & Documentation</TooltipContent>
        </Tooltip>

        {/* Theme Toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={toggleTheme}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
          </TooltipContent>
        </Tooltip>
      </div>
      <HelpDialog open={showHelp} onOpenChange={setShowHelp} />
    </div>
  );
}
